//Providers and Libraries
import { FC, useState, useEffect } from "react";
import { useAuth } from "src/contexts";
import { useOrgService, useReferences } from "src/services";
import { Dialog, Grid } from "@mui/material";
import { API } from "src/apis/apiWrapper";

//Interfaces
import { Draft } from "src/types/common";
import { IAIRecord, IAIModel } from "src/types/org/ai";
import { IOrgModel } from "src/types/org/orgInterface";
import { INote } from "src/types";
import { IOpsRequest } from "src/types/org/operation/requestInterface";
import { IOrgSearch, ISearchOptions } from "src/services/org";
import { ISummary } from "src/types";

//Processing
import { ValidationError, ErrorManager, ERRORS_EMPTY } from "src/types/validation";
import { validate } from "src/types/org/ai/aiRecordSchema";
import { processResponse } from "src/utils/api";
import { debounce } from "src/utils";
import { statusUpdate } from "src/services/schoolRequest";
import { disableDismiss, enableDismiss } from "src/generic/utils/modal";

//Components
import {
  FieldRow,
  FieldLabel,
  Title,
  ItemCard,
  IconButton,
  ErrorNotice,
  ContainerInline,
  HeaderInline,
  Loader,
  ItemRow,
  Divider,
} from "src/generic/component";
import { OrgFields } from "./orgFields";
import { AiFields } from "./aiFields";
import { PrimaryButton, Button } from "@cb/apricot-react";
import { OrgSearch } from "src/org/component/orgSearch";
import { DuplicateAIConfirmation } from "./duplicateAiConfirm";
import { Note } from "src/components/admin/elements/note";
import { Dropdown, Input, TextArea } from "src/generic/component/form";

// Styling and Icons
import CheckIcon from "@mui/icons-material/Check";
import { XpmRejectForm } from "./xpmRejectForm";
import { usStateOptions } from "../../../constants";

interface IAiEditorProps {
  stage: "prepare" | "execute" | "completed";
  action: "create" | "update";
  notes: INote[];
  hints: any;
  initial: Draft<IOpsRequest<IAIRecord>>;
  summary: ISummary;
  onComplete: (data: any) => void;
  onClose: () => void;
  // initial: Draft<IAIRecord>; // Singular property to represent both opening an existing request or mapped submissions
  // submitted: Draft<IAIRecord>;
}

export const rejectRequest = async (summary: ISummary, reason: string, user: string) => {
  if (!reason) throw new Error("Reason is required.");

  return await statusUpdate(summary.userId, summary.categoryId, {
    status: "xpmDenied",
    comment: reason,
    user: user,
  });
};

/**
 * This form is responsible for managing insertions and updates for AI
 * records exclusively.  I'm not a fan of this form requiring a summary
 * record to perform updates to the data because the summary is not
 * relevant to the data being modified and is only needed for a few
 * pieces of functionality.  Should migrate this functionality to
 * a parent form and handle all summary record processing there, which
 * will make this a stateless component that broadcasts data change upwards
 */
export const AiForm: FC<IAiEditorProps> = ({ onComplete, onClose, summary, action, notes, stage, initial, hints }) => {
  const { countries } = useReferences() || {}
  const { lastName, firstName, roles } = useAuth();
  const { findAi, findOrgById } = useOrgService();

  /**
   * Data managed by form for submission
   */
  const [org, setOrg] = useState<Draft<IOrgModel>>({});
  const [ai, setAi] = useState<Draft<IAIModel>>({});
  const [original, setOriginal] = useState<Draft<IAIRecord> | undefined>(undefined);
  const [comment, setComment] = useState<string | undefined>(undefined);
  const submitted = initial.submitted;
  const [orgSearchParams, setOrgSearchParams] = useState<any>({});
  const { types, subTypes } = useReferences();

  useEffect(() => {
    const data = initial.request;
    setOrg(data?.org || {});
    setAi(data?.ai || {});
    setOriginal(initial.original);
    setComment(initial.comment);
    updateSearch(data?.org || {});
  }, [initial]);

  /**
   * Form control state
   */
  const [isRejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [isNoteOpen, setNoteOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | React.ReactNode>("");
  const [errorMessageOpen, setErrorMessageOpen] = useState<boolean>(false);
  const [existingConfirmOpen, setExistingConfirmOpen] = useState<boolean>(false);
  const [reviewConfirmOpen, setReviewOpen] = useState<boolean>(false);
  const [matchedRecord, setMatchedRecord] = useState<IOrgSearch | undefined>(undefined);
  const [searchParams, setSearchParams] = useState<Partial<ISearchOptions>>({
    name: org?.name,
    zip: org?.visitingAddress?.zip5,
    state: org?.visitingAddress?.stateCd || org?.visitingAddress?.province,
    intl_postal_cd: org?.visitingAddress?.intlPostalCd,
    city: org.visitingAddress?.city,
    address: org.visitingAddress?.streetAddr1,
    aistatus: "all",
  });
  const updateSearch = (org: Draft<IOrgModel>) => {
    if (allowOrgChange) {
      debounce(() => {
        const nameChanged = org.name !== searchParams.name;
        const zipChanged = org.visitingAddress?.zip5 !== searchParams.zip;
        const postalChanged = org.visitingAddress?.intlPostalCd !== searchParams.intl_postal_cd;
        const addressChanged = org.visitingAddress?.streetAddr1 !== searchParams.address;
        const cityChanged = org.visitingAddress?.city !== searchParams.city;
        const stateChanged = org?.visitingAddress?.stateCd !== searchParams.state;

        if (nameChanged || zipChanged || postalChanged || addressChanged || cityChanged || stateChanged) {
          setSearchParams({
            name: org.name,
            address: org.visitingAddress?.streetAddr1,
            city: org.visitingAddress?.city,
            state: org.visitingAddress?.stateCd,
            zip: org.visitingAddress?.zip5,
            intl_postal_cd: org.visitingAddress?.intlPostalCd,
          });
        }
      });
    }
  };

  const originalOrg = original?.org;
  const isAdmin = roles.includes("XPM_ADMIN");

  /**
   * Form Control Permissions
   */
  const preparing = stage === "prepare";
  const completed = stage === "completed";
  const allowProtected = isAdmin;
  const readOnly = completed || (!preparing && !allowProtected);
  const allowOrgChange = action === "create" && stage === "execute" && allowProtected;

  const levelCd = submitted?.ai?.aiLevelCd;
  const submittedLevelCd = typeof levelCd === "string" ? parseInt(levelCd) : levelCd;
  const diffLevelCd = submittedLevelCd && submittedLevelCd !== ai.aiLevelCd;
  const requireComment = diffLevelCd;
  const requireConfirm = action === "create" && stage === "execute" && !originalOrg?.orgId;//orginalOrg's orgId is undefined or null

  // Post okta migrations, userNames now start with 'Z9Z9P',replacing preparedBy/approvedy with lastName, firstName
  const displayUserName = lastName + ', ' + firstName;
  const preparedBy = preparing ? displayUserName : initial.meta?.preparedBy || displayUserName;
  const approvedBy = displayUserName; //approvedBy can be set only once when approving the ops form and will be set to loggedin XPM_ADMIN role's username

  // Conditions for showing loader - submitting, retrieving org, retrieving files?
  const showLoading = submitting;
  const statusesToMoveToQC = ['processing', 'xpmDenied', 'appeal', 'international', 'testSecurity', 'deactivationApproval'];
  // ?? '' is nullish coalescing operator which gives default value (right side)if  left side is null or undefined
  const submitLabel = (statusesToMoveToQC.includes(summary?.processStatus ?? '' ))? 'Ready for QC' : 'Submit';

  /**
   * Error and Validation Management
   */
  let errors = ErrorManager(ERRORS_EMPTY);
  let request: IOpsRequest<IAIRecord> | undefined = undefined;
  let validated: boolean = false;
  try {
    const data = validate(
      {
        org: org,
        ai: ai,
      },
      stage === "prepare"
    );
    request = {
      request: data,
      submitted: initial.submitted || {}, //needs to be able to be undefined
      original: original,
      meta: {
        preparedBy: preparedBy,
        ...(stage !== "prepare" && { approvedBy: approvedBy }), //approvedBy should NOT be set for prepareOperationRequest
      },
    };
    validated = true;
  } catch (error: any) {
    if (error instanceof ValidationError) {
      errors = ErrorManager(error.fieldErrors);
    }
  }

  const saveDraft = async () => {
    const data = {
      org: org,
      ai: ai,
    };
    const draft = {
      request: data,
      submitted: initial.submitted || {}, //needs to be able to be undefined
      original: original,
      meta: {
        preparedBy: preparedBy,
        ...(stage !== "prepare" && { approvedBy: approvedBy }), //approvedBy should NOT be set for prepareOperationRequest
      },
    };
    setSubmitting(true);
    try {
      const response: IOrgModel = await API.post("AiFormRequest", `/save?key=saveDraft`, {
        body: {
          categoryId: summary.categoryId,
          userId: summary.userId,
          comment: comment,
          ...draft,
        },
      }).then((response: any) => processResponse(response));
    } catch (error: any) {
      setErrorMessage(error?.message || error);
      setErrorMessageOpen(true);
      console.log(error);
    }
    setSubmitting(false);
  };

  const submit = async () => {
    setSubmitting(true);

    try {
      if (validated) {
        if (stage === "completed") throw new Error("Cannot perform actions on a completed application");

        if (requireComment && !comment) throw new Error("Comment is required.");

        const action = stage === "prepare" ? "prepareOperationRequest" : "completeOperationRequest";
        const response: IOrgModel = await API.post("AiFormRequest", `/save?key=${action}`, {
          body: {
            categoryId: summary.categoryId,
            userId: summary.userId,
            comment: comment,
            currentProcessStatus: summary.processStatus,
            ...request,
          },
        }).then((response: any) => processResponse(response));
        onComplete(response);
      } else {
        throw new Error("The save failed as some of the entered fields are invalid.");
      }
    } catch (error: any) {
      setErrorMessage(error?.message || error);
      setErrorMessageOpen(true);
      console.log(error);
    }
    // Perform Redirect

    setSubmitting(false);
  };

  const removeOriginal = () => {
    setOriginal(undefined);
    setOrg({ ...org, orgId: undefined });
    setAi({ ...ai, aiCode: undefined });
  };

  const selectOrg = async (orgId: string) => {
    setSubmitting(true);
    setErrorMessage(undefined);
    try {
      const data = await findOrgById(orgId);
      const overwriteFields = {
        nameShortAbbr: data.org?.nameShortAbbr,
        nameNick: data.org?.nameNick,
        nameFormerOfficial: data.org?.nameFormerOfficial,
        nameRoot: data.org?.nameRoot,
        schoolSubTypeCd: data.org?.schoolSubTypeCd,
        schoolLevelCd: data.org?.schoolLevelCd,
        mdrPid: data.org?.mdrPid,
        ncesId: data.org?.ncesId,
        ipedsId: data.org?.ipedsId,
        feeWaiverId: data.org?.feeWaiverId,
        orgCodeNotes: data.org?.orgCodeNotes,
      };
      // Per new requirements, we need to prepopulate/overwrite the mdr,nces,ipeds,feewaiver etc fields from
      // the selected org from list of orgs displayed. Rest of fields are populated with original org data.
      setOrg({ ...org, orgId: orgId, ...overwriteFields });
      setOriginal({
        org: data.org,
      });
    } catch (error: any) {
      setErrorMessage(error?.message || error);
      setErrorMessageOpen(true);
    }
    setSubmitting(false);
  };

  const selectAi = async (aiCode: string, orgId: string) => {
    setSubmitting(true);
    setErrorMessage(undefined);
    try {
      const aiRecord = await findAi(aiCode, orgId);
      const overwriteFields = {
        nameShortAbbr: aiRecord.org?.nameShortAbbr,
        nameNick: aiRecord.org?.nameNick,
        nameFormerOfficial: aiRecord.org?.nameFormerOfficial,
        nameRoot: aiRecord.org?.nameRoot,
        schoolSubTypeCd: aiRecord.org?.schoolSubTypeCd,
        schoolLevelCd: aiRecord.org?.schoolLevelCd,
        mdrPid: aiRecord.org?.mdrPid,
        ncesId: aiRecord.org?.ncesId,
        ipedsId: aiRecord.org?.ipedsId,
        feeWaiverId: aiRecord.org?.feeWaiverId,
        orgCodeNotes: aiRecord.org?.orgCodeNotes,
      };
      // Per new requirements, we need to prepopulate/overwrite the mdr,nces,ipeds,feewaiver etc fields from
      // the selected org from list of orgs displayed. Rest of fields are populated with original org data.
      setOrg({ ...org, orgId: orgId, ...overwriteFields });
      setAi({ ...aiRecord.ai, ...ai });
      setOriginal(aiRecord);
    } catch (error: any) {
      setErrorMessage(error?.message || error);
      setErrorMessageOpen(true);
    }
    setSubmitting(false);
  };

  return (
    <>
      <Dialog fullWidth={true} maxWidth={"md"} open={showLoading} onClose={disableDismiss}>
        <div style={{ padding: "20px" }}>
          <Loader />
        </div>
      </Dialog>

      {errorMessage && (
        <div style={{ marginBottom: "20px", marginTop: "20px" }}>
          <ErrorNotice errorMessage={errorMessage} />
        </div>
      )}
      <Dialog
        fullWidth={true}
        maxWidth={"md"}
        open={errorMessageOpen}
        onClose={enableDismiss((event, reason) => {
          setErrorMessageOpen(false);
        })}
      >
        <ContainerInline style={{ padding: "20px" }}>
          <HeaderInline style={{ marginBottom: "6px" }}>
            <i style={{ marginRight: "10px" }} className="cb-icon cb-icon-circular cb-exclamation cb-red1-color" aria-hidden="true"></i>
            <h4 className="cb-notification-title">
              <strong>Errors Occurred</strong>
            </h4>
          </HeaderInline>
          <div style={{ marginLeft: "42px" }} className="cb-notification-content">
            {errorMessage}
          </div>
        </ContainerInline>
      </Dialog>

      <Dialog fullWidth={true} maxWidth="md" open={reviewConfirmOpen} onClose={() => {}}>
        <ContainerInline style={{ padding: "20px" }}>
          <h4 style={{ marginBottom: "10px" }}>Possible Duplicates</h4>
          <OrgSearch
            params={{
              name: org.name,
              orgid: org.orgId,
              state: org.visitingAddress?.countryIsoCd === "US" ? org?.visitingAddress?.stateCd : undefined,
              intl_postal_cd: org.visitingAddress?.countryIsoCd === "US" ? undefined : org.visitingAddress?.intlPostalCd,
              iso: org.visitingAddress?.countryIsoCd,
              size: "100", //default size is 20
            }}
            onSelect={async (match) => {
              if (match && match.ais && match.ais.length > 0) {
                setExistingConfirmOpen(true);
                setMatchedRecord(match);
              } else {
                selectOrg(match.orgId);
              }
              setReviewOpen(false);
            }}
          ></OrgSearch>
          <PrimaryButton
            style={{ width: "100%", marginBottom: "10px" }}
            onClick={() => {
              setReviewOpen(false);
              submit();
            }}
            disabled={submitting}
          >
            Confirm & Proceed
          </PrimaryButton>
          <Button
            style={{ marginBottom: "10px", width: "100%" }}
            onClick={() => {
              setReviewOpen(false);
            }}
            disabled={submitting}
          >
            Close
          </Button>
        </ContainerInline>
      </Dialog>

      {!originalOrg && allowOrgChange && !readOnly && (
        <div>
          <Title>Search for Organization</Title>
          <FieldRow>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Input
                  onChange={(e) => {
                    setOrgSearchParams({ ...orgSearchParams, name: e.target.value });
                  }}
                  placeholder="Organization Name"
                  value={orgSearchParams.name}
                />
              </Grid>
              <Grid item xs={6}>
                <Input
                  onChange={(e) => {
                    setOrgSearchParams({ ...orgSearchParams, text: e.target.value });
                  }}
                  placeholder="Name, Addresses, City..."
                  value={orgSearchParams.text}
                />
              </Grid>
              <Grid item xs={6}>
                <FieldLabel variant="body1">Organization Type</FieldLabel>
                <Dropdown
                  values={types}
                  onChange={(e) => {
                    setOrgSearchParams({ ...orgSearchParams, orgtype: e.target.value });
                  }}
                  value={orgSearchParams.orgtype}
                ></Dropdown>
              </Grid>
              <Grid item xs={6}>
                <FieldLabel variant="body1">Organization Sub-Type</FieldLabel>
                <Dropdown
                  values={subTypes.filter(
                    (item) =>
                      item.parentId === (isNaN(parseInt(orgSearchParams.orgtype)) ? undefined : parseInt(orgSearchParams.orgtype)) || !item.parentId
                  )}
                  onChange={(e) => {
                    setOrgSearchParams({ ...orgSearchParams, orgsubtype: e.target.value });
                  }}
                  value={orgSearchParams.orgsubtype}
                ></Dropdown>
              </Grid>
              <Grid item xs={4}>
                <div style={{ marginTop: "25px" }}>
                  <Input
                    onChange={(e) => {
                      setOrgSearchParams({ ...orgSearchParams, orgid: e.target.value });
                    }}
                    placeholder="Organization Id"
                    value={orgSearchParams.orgid}
                  />
                </div>
              </Grid>
              <Grid item xs={4}>
                <FieldLabel variant="body1">Select Country</FieldLabel>
                {countries && <Dropdown
                  values={countries.map((option) => ({ key: option.value, label: option.label }))}
                  onChange={(e) => {
                    setOrgSearchParams({
                      ...orgSearchParams,
                      countryIsoCd: e.target.value,
                      state: undefined,
                      postalCd: undefined,
                    });
                  }}
                  value={orgSearchParams.countryIsoCd}
                />}
              </Grid>
              {orgSearchParams.countryIsoCd && (
                <Grid item xs={4}>
                  <div>
                    {orgSearchParams.countryIsoCd === "US" ? (
                      <div>
                        <FieldLabel variant="body1">Select State</FieldLabel>
                        <Dropdown
                          values={usStateOptions.map((option) => ({ key: option.value, label: option.label }))}
                          onChange={(e) => {
                            setOrgSearchParams({ ...orgSearchParams, state: e.target.value, postalCd: undefined });
                          }}
                          value={orgSearchParams.state}
                        />
                      </div>
                    ) : (
                      <div style={{ marginTop: "25px" }}>
                        <Input
                          onChange={(e) => {
                            setOrgSearchParams({ ...orgSearchParams, state: undefined, postalCd: e.target.value });
                          }}
                          placeholder="Postal Code"
                          value={orgSearchParams.postalCd}
                        />
                      </div>
                    )}
                  </div>
                </Grid>
              )}
            </Grid>
          </FieldRow>
          <Divider />
          <OrgSearch
            params={{
              name: orgSearchParams.name,
              orgid: orgSearchParams.orgid,
              text: orgSearchParams.text,
              state: orgSearchParams.state,
              intl_postal_cd: orgSearchParams.postalCd,
              iso: orgSearchParams.countryIsoCd,
              orgtype: orgSearchParams.orgtype,
              orgsubtype: orgSearchParams.orgsubtype,
              size: "500", //default size is 20
            }}
            onSelect={async (match) => {
              if (match && match.ais && match.ais.length> 0) {
                setExistingConfirmOpen(true);
                setMatchedRecord(match);
              } else {
                selectOrg(match.orgId);
              }
            }}
          ></OrgSearch>
        </div>
      )}

      {originalOrg && (
        <>
          <Title>Selected Organization</Title>
          <ItemRow>
            <ItemCard>
              <div>
                <div>
                  <strong>
                    {originalOrg.name} (OrgId: {originalOrg.orgId})
                  </strong>
                  <div>{originalOrg.visitingAddress?.streetAddr1}</div>
                  <div>
                    {originalOrg.visitingAddress?.city}
                    {originalOrg.visitingAddress?.stateCd && (
                      <>
                        ,&nbsp;{originalOrg.visitingAddress.stateCd} {originalOrg.visitingAddress.zip5}
                      </>
                    )}
                  </div>
                </div>
                {allowOrgChange && (
                  <IconButton
                    onClick={() => {
                      removeOriginal();
                    }}
                  >
                    Change
                  </IconButton>
                )}
              </div>
            </ItemCard>
          </ItemRow>
        </>
      )}

      <Dialog fullWidth={true} maxWidth={"md"} open={existingConfirmOpen} onClose={disableDismiss}>
        <div style={{ padding: "20px" }}>
          {matchedRecord && (
            <>
              <FieldRow>
                <IconButton
                  onClick={() => {
                    setNoteOpen(true);
                  }}
                >
                  <CheckIcon /> Review Notes ({(notes || []).length})
                </IconButton>
              </FieldRow>
              <DuplicateAIConfirmation
                allowReject={true}
                match={matchedRecord}
                onConfirm={async (match) => {
                  setExistingConfirmOpen(false);
                  if (match) {
                    const matchedAi = (match.ais || [])[0];
                    const aiCode = matchedAi.aiCode;
                    const orgId = match.orgId;
                    selectAi(aiCode, orgId);
                  }
                }}
                onCancel={() => {
                  setExistingConfirmOpen(false);
                }}
                onReject={(reason: string) => {
                  setRejectOpen(true);
                  setRejectReason(reason);
                }}
              />
            </>
          )}
        </div>
      </Dialog>

      <IconButton
        onClick={() => {
          setNoteOpen(true);
        }}
      >
        <CheckIcon /> Review Notes ({(notes || []).length})
      </IconButton>
      <Dialog
        fullWidth={true}
        maxWidth={"md"}
        open={isNoteOpen}
        onClose={enableDismiss(() => {
          setNoteOpen(false);
        })}
      >
        <div style={{ padding: "20px" }}>
          <Title style={{ marginBottom: "10px" }}>Vetting Notes</Title>
          {(notes || []).map((note) => {
            return <Note maxWidth="auto" note={note}></Note>;
          })}
        </div>
      </Dialog>

      <OrgFields
        item={org}
        original={original?.org || undefined}
        defaults={{}}
        hints={hints?.org || {}}
        errors={errors.getFieldError("org")}
        showProtected={allowProtected}
        readOnly={readOnly}
        type="ai"
        onUpdate={(data) => {
          updateSearch(data);
          setOrg(data);
        }}
      ></OrgFields>
      <AiFields
        item={ai}
        original={original?.ai || undefined}
        hints={hints?.ai || {}}
        errors={errors.getFieldError("ai")}
        showProtected={allowProtected}
        readOnly={readOnly}
        onUpdate={(data) => {
          setAi(data);
        }}
      ></AiFields>
      <br />

      <FieldRow>
        <FieldLabel>Comment to Submitter</FieldLabel>
        <TextArea
          validation={requireComment && !comment ? "error" : undefined}
          validationMsg={requireComment && !comment ? "Comment is required." : ""}
          readOnly={readOnly}
          onChange={(e) => {
            setComment(e.target.value);
          }}
          value={comment}
        />
      </FieldRow>

      <div>
        {!readOnly && (
          <>
            {requireConfirm ? (
              <PrimaryButton
                style={{ width: "100%", marginBottom: "10px" }}
                onClick={() => {
                  if (validated) {
                    setReviewOpen(true);
                  }
                }}
                disabled={submitting || !validated}
              >
                Review
              </PrimaryButton>
            ) : (
              <PrimaryButton
                style={{ width: "100%", marginBottom: "10px" }}
                onClick={() => {
                  submit();
                }}
                disabled={submitting}
              >
                {submitLabel}
              </PrimaryButton>
            )}
            { summary.processStatus !== 'qcReadyVetting' && (
            <>
              <Button
                style={{ marginBottom: "10px", width: "100%" }}
                onClick={() => {
                  saveDraft();
                }}
                disabled={submitting}
              >
                Save Draft
              </Button>
            </>)}
            {stage === "execute" && (
              <>
                <Button
                  style={{ width: "100%", marginBottom: "10px" }}
                  onClick={() => {
                    setRejectOpen(true);
                  }}
                  disabled={submitting}
                >
                  Reject
                </Button>
                <Dialog fullWidth={true} maxWidth={"md"} open={isRejectOpen} onClose={disableDismiss}>
                  <div style={{ padding: "20px" }}>
                    <XpmRejectForm
                      summary={summary}
                      reason={rejectReason}
                      onComplete={(summary) => {
                        setRejectOpen(false);
                        setExistingConfirmOpen(false);
                        onClose();
                      }}
                      onClose={() => {
                        setRejectOpen(false);
                      }}
                    />
                  </div>
                </Dialog>
              </>
            )}
          </>
        )}

        <Button
          style={{ marginBottom: "10px", width: "100%" }}
          onClick={() => {
            onClose();
          }}
          disabled={submitting}
        >
          Close
        </Button>
      </div>
    </>
  );
};
